import { DateTime } from 'luxon';

/**
 * Robust deep cloning utility tailored for Reservatoo state and form management.
 *
 * Key guarantees:
 * 1. Preserves Luxon DateTime instances intact without invoking .toJSON() or stripping prototypes.
 * 2. Unwraps Svelte 5 reactive proxies ($state) into isolated, plain objects.
 * 3. Deeply clones arrays and plain objects to prevent reference leaking.
 * 4. Safely clones standard types (Date, RegExp, Map, Set).
 * 5. Handles circular references cleanly via WeakMap.
 * 6. Immune to DataCloneError (leaves DOM nodes, Window references, and functions untouched).
 */
export function deepClone<T>(value: T, seen = new WeakMap<object, object>()): T {
	if (value === null || typeof value !== 'object') {
		return value;
	}

	// 1. Luxon DateTime is 100% immutable by design; preserving reference is safe and preserves methods
	if (value instanceof DateTime) {
		return value;
	}

	// 2. Native mutable types
	if (value instanceof Date) {
		return new Date(value.getTime()) as T;
	}

	if (value instanceof RegExp) {
		return new RegExp(value.source, value.flags) as T;
	}

	// 3. Circular reference protection
	if (seen.has(value)) {
		return seen.get(value) as T;
	}

	// 4. Arrays
	if (Array.isArray(value)) {
		const copy: unknown[] = [];
		seen.set(value, copy);
		for (let i = 0; i < value.length; i++) {
			copy[i] = deepClone(value[i], seen);
		}
		return copy as T;
	}

	// 5. Sets
	if (value instanceof Set) {
		const copy = new Set<unknown>();
		seen.set(value, copy);
		for (const item of value) {
			copy.add(deepClone(item, seen));
		}
		return copy as T;
	}

	// 6. Maps
	if (value instanceof Map) {
		const copy = new Map<unknown, unknown>();
		seen.set(value, copy);
		for (const [k, v] of value) {
			copy.set(k, deepClone(v, seen));
		}
		return copy as T;
	}

	// 7. Non-plain objects (DOM elements, Window, EventTarget, or custom class instances)
	const proto = Object.getPrototypeOf(value);
	if (proto !== null && proto !== Object.prototype) {
		return value;
	}

	// 8. Plain objects and Svelte 5 reactive proxies
	const copy: Record<string, unknown> = {};
	seen.set(value, copy);
	for (const key of Object.keys(value as Record<string, unknown>)) {
		copy[key] = deepClone((value as Record<string, unknown>)[key], seen);
	}

	return copy as T;
}
